import React from "react";
import {
  Box,
  Typography,
  Switch,
  IconButton,
  Checkbox,
  Paper,
  alpha,
  useTheme,
  TextField,
  FormControlLabel,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export const TIKTOK_MANUAL_AGE_ORDER = [
  "13-17",
  "18-24",
  "25-34",
  "35-44",
  "45-54",
  "55-64",
  "65-",
] as const;

export type TiktokAudienceFormState = {
  male: string;
  female: string;
  unknown: string;
  ages: Record<string, string>;
};

/** Max allowed distance from 100% when summing manual TikTok audience fields. */
const TIKTOK_SUM_EPS = 0.01;

export function getTiktokGenderSum(form: TiktokAudienceFormState): number {
  const m = parseFloat(String(form.male ?? "").trim()) || 0;
  const f = parseFloat(String(form.female ?? "").trim()) || 0;
  const u = parseFloat(String(form.unknown ?? "").trim()) || 0;
  return Math.round((m + f + u) * 100) / 100;
}

export function getTiktokAgeSum(form: TiktokAudienceFormState): number {
  let s = 0;
  for (const r of TIKTOK_MANUAL_AGE_ORDER) {
    s += parseFloat(String(form.ages[r] ?? "").trim()) || 0;
  }
  return Math.round(s * 100) / 100;
}

export function tiktokGenderSectionHasInput(
  form: TiktokAudienceFormState,
): boolean {
  return [form.male, form.female, form.unknown].some(
    (v) => String(v ?? "").trim() !== "",
  );
}

export function tiktokAgeSectionHasInput(
  form: TiktokAudienceFormState,
): boolean {
  return TIKTOK_MANUAL_AGE_ORDER.some(
    (r) => String(form.ages[r] ?? "").trim() !== "",
  );
}

export function isTiktokAudienceFormValid(
  form: TiktokAudienceFormState,
): boolean {
  const g = tiktokGenderSectionHasInput(form);
  const a = tiktokAgeSectionHasInput(form);
  const gOk =
    !g || Math.abs(getTiktokGenderSum(form) - 100) <= TIKTOK_SUM_EPS;
  const aOk = !a || Math.abs(getTiktokAgeSum(form) - 100) <= TIKTOK_SUM_EPS;
  return gOk && aOk;
}

export type MediaKitPlatformCardProps = {
  platform: "youtube" | "instagram" | "tiktok" | "podscope";
  icon: React.ReactNode;
  expanded: boolean;
  setExpanded: (value: boolean) => void;
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  connectedPlatforms: Set<string>;
  isPlatformEnabled: (
    platform: "youtube" | "instagram" | "tiktok" | "podscope",
  ) => boolean;
  platformRequiresAccount: (platform: string) => boolean;
  handlePlatformToggle: (
    platform: "youtube" | "instagram" | "tiktok" | "podscope",
    enabled: boolean,
  ) => void;
  handleContentCountToggle: (
    platform: "youtube" | "instagram" | "tiktok" | "podscope",
    value: boolean,
  ) => void;
  handleMetricToggle: (
    platform: "youtube" | "instagram" | "tiktok" | "podscope",
    path: string[],
    metric: string,
    value: boolean,
  ) => void;
  tiktokAudienceForm: TiktokAudienceFormState;
  setTiktokAudienceForm: React.Dispatch<
    React.SetStateAction<TiktokAudienceFormState>
  >;
  metricLabels: any;
  sectionLabels: Record<string, string>;
  /** Shown under the platform name while collapsed (e.g. "8 de 12 métricas"). */
  summary?: string;
  /** Where to connect the platform account when it is missing. */
  connectAccountHref?: string;
};

/**
 * Module-level platform card so React does not treat it as a new component type
 * on every parent re-render (avoids losing focus in TikTok demographic inputs).
 */
export function MediaKitPlatformCard({
  platform,
  icon,
  expanded,
  setExpanded,
  formData,
  setFormData,
  connectedPlatforms,
  isPlatformEnabled,
  platformRequiresAccount,
  handlePlatformToggle,
  handleContentCountToggle,
  handleMetricToggle,
  tiktokAudienceForm,
  setTiktokAudienceForm,
  metricLabels,
  sectionLabels,
  summary,
  connectAccountHref,
}: MediaKitPlatformCardProps) {
  const theme = useTheme();
  const genderSum = getTiktokGenderSum(tiktokAudienceForm);
  const ageSum = getTiktokAgeSum(tiktokAudienceForm);
  const genderHasInput = tiktokGenderSectionHasInput(tiktokAudienceForm);
  const ageHasInput = tiktokAgeSectionHasInput(tiktokAudienceForm);
  const genderSumOk =
    !genderHasInput ||
    Math.abs(genderSum - 100) <= TIKTOK_SUM_EPS;
  const ageSumOk =
    !ageHasInput || Math.abs(ageSum - 100) <= TIKTOK_SUM_EPS;

  const platformEnabled = isPlatformEnabled(platform);
  const needsAccount = platformRequiresAccount(platform);
  const hasAccount = connectedPlatforms.has(platform);
  const isDisabled = needsAccount && !hasAccount;
  const sectionMap: Record<string, string[]> = {
    instagram: ["reach", "engagement", "audience"],
    tiktok: ["consumption", "engagement", "audience"],
  };
  const sections = sectionMap[platform] || [];
  const labels = metricLabels[platform];
  const primaryColor = theme.palette.primary.main;

  const renderMetricCheckboxes = (
    p: "youtube" | "instagram" | "tiktok" | "podscope",
    path: string[],
    metrics: Record<string, string>,
    platformEnabledInner: boolean,
  ) => {
    const platformBranch = formData.enabledMetrics?.[p] as
      | Record<string, unknown>
      | undefined;
    let enabledMetrics: Record<string, unknown> = platformBranch || {};
    for (const seg of path) {
      enabledMetrics = (enabledMetrics[seg] as Record<string, unknown>) || {};
    }

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {Object.entries(metrics).map(([metricKey, label]) => {
          const isChecked =
            (enabledMetrics[metricKey] as boolean | undefined) ?? true;
          return (
            <FormControlLabel
              key={metricKey}
              control={
                <Checkbox
                  size="small"
                  checked={isChecked && platformEnabledInner}
                  disabled={!platformEnabledInner}
                  onChange={(e) =>
                    handleMetricToggle(
                      p,
                      path,
                      metricKey,
                      e.target.checked,
                    )
                  }
                  color="primary"
                  sx={{ p: 0.5 }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.8rem",
                    color: platformEnabledInner
                      ? "text.primary"
                      : "text.disabled",
                  }}
                >
                  {label}
                </Typography>
              }
              sx={{
                m: 0,
                mr: 2,
                mb: 0.5,
              }}
            />
          );
        })}
      </Box>
    );
  };

  const makeFlagToggle = (
    subPath: string | null,
    field: string,
    label: string,
  ) => {
    const checked = subPath
      ? !!(formData.enabledMetrics as any)?.[platform]?.[subPath]?.[field]
      : !!(formData.enabledMetrics as any)?.[platform]?.[field];
    const onChange = (value: boolean) => {
      if (subPath) {
        setFormData((prev: any) => ({
          ...prev,
          enabledMetrics: {
            ...prev.enabledMetrics,
            [platform]: {
              ...(prev.enabledMetrics?.[platform] || {}),
              [subPath]: {
                ...(prev.enabledMetrics?.[platform]?.[subPath] || {}),
                [field]: value,
              },
            },
          },
        }));
      } else {
        handleContentCountToggle(platform, value);
      }
    };
    return (
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={checked}
            disabled={!platformEnabled}
            onChange={(e) => onChange(e.target.checked)}
            color="primary"
            sx={{ p: 0.5 }}
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              color: platformEnabled ? "text.primary" : "text.disabled",
            }}
          >
            {label}
          </Typography>
        }
        sx={{
          m: 0,
          mr: 2,
          mb: 0.5,
        }}
      />
    );
  };

  const makeContentCountToggle = (subPath: string | null, label: string) =>
    makeFlagToggle(subPath, "showContentCount", label);

  const contentCountToggle = makeContentCountToggle(
    null,
    "Cantidad de contenido",
  );
  const longFormCountToggle = makeContentCountToggle(
    "longForm",
    "Cantidad de contenido",
  );
  const longFormPdfTableToggle = makeFlagToggle(
    "longForm",
    "showPdfVideoTable",
    "Tabla de videos en el PDF",
  );
  const shortsCountToggle = makeContentCountToggle(
    "shorts",
    "Cantidad de contenido",
  );
  const podscopeLongFormCountToggle = makeContentCountToggle(
    "longForm",
    "Cantidad de contenido",
  );
  const podscopeStreamingCountToggle = makeContentCountToggle(
    "streaming",
    "Cantidad de contenido",
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        overflow: "hidden",
        borderColor:
          platformEnabled && !isDisabled ? "primary.main" : "divider",
        borderWidth: platformEnabled && !isDisabled ? 2 : 1,
        transition: "border-color 0.2s",
        opacity: isDisabled ? 0.7 : 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          backgroundColor:
            platformEnabled && !isDisabled
              ? alpha(primaryColor, 0.04)
              : "transparent",
          cursor: isDisabled ? "default" : "pointer",
          "&:hover": {
            backgroundColor: isDisabled
              ? "transparent"
              : alpha(primaryColor, 0.08),
          },
        }}
        onClick={() => !isDisabled && setExpanded(!expanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
          <Box
            sx={{
              color:
                platformEnabled && !isDisabled
                  ? "primary.main"
                  : "text.disabled",
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                color:
                  platformEnabled && !isDisabled
                    ? "text.primary"
                    : "text.disabled",
              }}
            >
              {platform === "youtube"
                ? "YouTube"
                : platform === "instagram"
                  ? "Instagram"
                  : platform === "tiktok"
                    ? "TikTok"
                    : "Podscope"}
            </Typography>
            {isDisabled ? (
              <Typography variant="caption" color="text.secondary">
                Sin cuenta conectada
                {connectAccountHref ? (
                  <>
                    {" · "}
                    <Link
                      component={RouterLink}
                      to={connectAccountHref}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Conectar
                    </Link>
                  </>
                ) : null}
              </Typography>
            ) : summary && !expanded ? (
              <Typography variant="caption" color="text.secondary">
                {summary}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Switch
          checked={platformEnabled && !isDisabled}
          disabled={isDisabled}
          onChange={(e) => {
            e.stopPropagation();
            handlePlatformToggle(platform, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          color="primary"
        />

        {!isDisabled && (
          <IconButton size="small" sx={{ ml: 1 }}>
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        )}
      </Box>

      {expanded && !isDisabled && (
        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
          {platform === "youtube" ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  YouTube Videos
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    {sectionLabels.consumption}
                  </Typography>
                  {renderMetricCheckboxes(
                    platform,
                    ["longForm", "consumption"],
                    metricLabels.youtube.longForm.consumption,
                    platformEnabled,
                  )}
                  {longFormCountToggle}
                  {longFormPdfTableToggle}
                  <Box sx={{ mt: 1 }}>
                    {renderMetricCheckboxes(
                      platform,
                      ["longForm", "engagement"],
                      metricLabels.youtube.longForm.engagement,
                      platformEnabled,
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    {sectionLabels.audience}
                  </Typography>
                  {renderMetricCheckboxes(
                    platform,
                    ["longForm", "audience"],
                    metricLabels.youtube.longForm.audience,
                    platformEnabled,
                  )}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  YouTube Shorts
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  {sectionLabels.consumption}
                </Typography>
                {renderMetricCheckboxes(
                  platform,
                  ["shorts", "consumption"],
                  metricLabels.youtube.shorts.consumption,
                  platformEnabled,
                )}
                {shortsCountToggle}
              </Box>
            </>
          ) : platform === "podscope" ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  YouTube Videos
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  {sectionLabels.consumption}
                </Typography>
                {renderMetricCheckboxes(
                  platform,
                  ["longForm", "consumption"],
                  metricLabels.podscope.longForm.consumption,
                  platformEnabled,
                )}
                {podscopeLongFormCountToggle}
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  YouTube Shorts
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  {sectionLabels.consumption}
                </Typography>
                {renderMetricCheckboxes(
                  platform,
                  ["shorts", "consumption"],
                  metricLabels.podscope.shorts.consumption,
                  platformEnabled,
                )}
              </Box>
              {(["streaming", "brands"] as const).map((section, idx) => (
                <Box key={section} sx={{ mb: idx < 1 ? 2 : 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    {sectionLabels[section]}
                  </Typography>
                  {renderMetricCheckboxes(
                    platform,
                    [section],
                    (metricLabels.podscope as typeof metricLabels.podscope)[
                      section
                    ],
                    platformEnabled,
                  )}
                  {section === "streaming" && podscopeStreamingCountToggle}
                </Box>
              ))}
            </>
          ) : (
            <>
              {sections.map((section, idx) => (
                <Box
                  key={section}
                  sx={{ mb: idx < sections.length - 1 ? 2 : 0 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    {sectionLabels[section as keyof typeof sectionLabels]}
                  </Typography>
                  {renderMetricCheckboxes(
                    platform,
                    [section],
                    labels[section as keyof typeof labels] as Record<
                      string,
                      string
                    >,
                    platformEnabled,
                  )}
                  {(section === "consumption" ||
                    (platform === "instagram" && idx === 0)) &&
                    contentCountToggle}
                </Box>
              ))}
              {platform === "tiktok" && (
                <Box
                  sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    Audiencia TikTok (autorreportada): ingresa porcentajes 0–100.
                    Se mostrará en el media kit público con esa etiqueta.
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    Género (%) — debe sumar 100%
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: genderHasInput ? 0.5 : 2,
                    }}
                  >
                    <TextField
                      size="small"
                      label="Masculino"
                      type="number"
                      value={tiktokAudienceForm.male}
                      onChange={(e) =>
                        setTiktokAudienceForm((prev) => ({
                          ...prev,
                          male: e.target.value,
                        }))
                      }
                      disabled={!platformEnabled}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      size="small"
                      label="Femenino"
                      type="number"
                      value={tiktokAudienceForm.female}
                      onChange={(e) =>
                        setTiktokAudienceForm((prev) => ({
                          ...prev,
                          female: e.target.value,
                        }))
                      }
                      disabled={!platformEnabled}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      size="small"
                      label="Otro"
                      type="number"
                      value={tiktokAudienceForm.unknown}
                      onChange={(e) =>
                        setTiktokAudienceForm((prev) => ({
                          ...prev,
                          unknown: e.target.value,
                        }))
                      }
                      disabled={!platformEnabled}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {genderHasInput && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 2,
                        color: genderSumOk ? "success.main" : "error.main",
                        fontWeight: 600,
                      }}
                    >
                      Total género: {genderSum.toFixed(1)}%
                      {!genderSumOk ? " — ajusta para que sume 100%." : ""}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    Edad (%) — debe sumar 100%
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: ageHasInput ? 0.5 : 0,
                    }}
                  >
                    {TIKTOK_MANUAL_AGE_ORDER.map((range) => (
                      <TextField
                        key={range}
                        size="small"
                        label={range === "65-" ? "65+ años" : `${range} años`}
                        type="number"
                        value={tiktokAudienceForm.ages[range] ?? ""}
                        onChange={(e) =>
                          setTiktokAudienceForm((prev) => ({
                            ...prev,
                            ages: {
                              ...prev.ages,
                              [range]: e.target.value,
                            },
                          }))
                        }
                        disabled={!platformEnabled}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        sx={{ width: 120 }}
                      />
                    ))}
                  </Box>
                  {ageHasInput && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        color: ageSumOk ? "success.main" : "error.main",
                        fontWeight: 600,
                      }}
                    >
                      Total edad: {ageSum.toFixed(1)}%
                      {!ageSumOk ? " — ajusta para que sume 100%." : ""}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Paper>
  );
}
